<template>
  <Sheet v-model:open="isOpen">
    <SheetContent class="overflow-y-auto">
      <SheetHeader padded>
        <SheetTitle size="lg">Edit SMS Adapter Configuration</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded class="flex flex-col gap-3">
        <div>
          <Label class="text-sm font-medium text-gray-700">Provider</Label>
          <Select v-model="form.provider">
            <SelectTrigger class="mt-1 w-full">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="telnyx">Telnyx</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label class="text-sm font-medium text-gray-700">Status</Label>
          <Select v-model="form.status">
            <SelectTrigger class="mt-1 w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="active">Active</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label class="text-sm font-medium text-gray-700">Telnyx Phone Number</Label>
          <Input v-model="form.telnyx_phone_number" class="mt-1" placeholder="Telnyx phone number" />
        </div>

        <div>
          <Label class="text-sm font-medium text-gray-700">Telnyx Phone Number Order ID</Label>
          <Input v-model="form.telnyx_phone_number_order_id" class="mt-1" placeholder="Order ID" />
        </div>

        <div>
          <Label class="text-sm font-medium text-gray-700">Requested Area Code</Label>
          <Input v-model="form.requested_area_code" class="mt-1" type="number" placeholder="Area code" />
        </div>

        <Button class="mt-6" :disabled="isSaving" @click="handleSave">
          {{ isSaving ? 'Saving...' : 'Save' }}
        </Button>
      </SheetBody>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { hFetch } from '@/lib/hfetch';
import { toast } from 'vue-sonner';
import type { SmsAdapterConfig } from './types';

type Props = {
  open: boolean;
  config: SmsAdapterConfig;
  teamId: string;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  saved: [];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
});

const isSaving = ref(false);

const form = ref({
  provider: props.config.provider || '',
  status: props.config.status || 'inactive',
  telnyx_phone_number: props.config.telnyx_phone_number || '',
  telnyx_phone_number_order_id: props.config.telnyx_phone_number_order_id || '',
  requested_area_code: props.config.requested_area_code?.toString() || '',
});

const handleSave = async () => {
  isSaving.value = true;
  try {
    const payload = {
      ...form.value,
      requested_area_code: form.value.requested_area_code ? Number(form.value.requested_area_code) : null,
    };
    const result = await hFetch<SmsAdapterConfig, unknown>({
      url: `/api/admin/teams/${props.teamId}/sms_adapter_configuration`,
      method: 'PUT',
      payload: { sms_adapter_configuration: payload },
    });
    if (result.status === 'success') {
      toast.success('SMS Adapter Configuration updated');
      isOpen.value = false;
      emit('saved');
    } else {
      toast.error('Update failed', { description: result.messages.join(', ') });
    }
  } catch {
    toast.error('An error occurred while saving');
  } finally {
    isSaving.value = false;
  }
};
</script>
